<script lang="ts" setup>
import { computed } from "@vue/reactivity";
import { isObject } from "@vue/shared";
import { ref, watch } from "vue";
import { openModal, openPage, openSlideover } from "../../../../composables/dialog/drawer";
import MAction from "../../MAction";
import MBadge from "../../MBadge";
import MLink from "../../MLink/MLink.vue";
import { PropsOf } from "../../../../utils/types";

type DescriptionListItemValue = { text: string } & (TextValue | BadgeValue | LinkValue);

type TextValue = {
  type: "text";
};

type BadgeValue = {
  type: "badge";
} & PropsOf<typeof MBadge>;

type LinkValue = {
  type: "link";
} & PropsOf<typeof MLink>;

const props = defineProps<{
  label: string;
  value?: DescriptionListItemValue | string | number | null;

  darkLabel?: boolean;

  horizontal?: boolean;
  labelWidth?: string;

  presentationStyle?: "vertical-line" | "bullet";

  emptyValueText?: string;

  badge?:
    | string
    | InstanceType<typeof MBadge>["$props"]
    | Array<string | InstanceType<typeof MBadge>["$props"]>;

  action?: {
    icon?: string;
    srLabel?: string;
    to?: string;
    toSlideover?: string;
    toModal?: string;
  };
}>();

function handleClick(e: MouseEvent) {
  if (!props.action) return;

  e.stopPropagation();

  const { to, toSlideover, toModal } = props.action;
  if (to) {
    return openPage(to);
  }

  if (toModal) {
    const [url, options] = isObject(toModal) ? [toModal.url, toModal] : [toModal, {}];
    return openModal(url, options as any);
  }
  if (toSlideover) {
    const [url, options] = isObject(toSlideover)
      ? [toSlideover.url, toSlideover]
      : [toSlideover, {}];

    return openSlideover(url, options as any);
  }
}

// better align buttons otherwise it will align on the `dd` instead of description list item
const labelEl = ref<HTMLElement | null>(null);
const contentMarginTop = ref("23px");
watch(labelEl, (l) => {
  contentMarginTop.value = `-${l?.getBoundingClientRect()?.height ?? 23}px`;
});

const extraRows = computed(() => [props.badge, props.value].filter((x) => !!x).length);
</script>
<template>
  <div
    class="description-list-item"
    :class="{
      'has-click': !!action,
      'has-vertical-line': presentationStyle == 'vertical-line',
      'is-horizontal': horizontal,
      'dark-label': props.darkLabel,
    }"
    :style="
      horizontal && labelWidth ? { '--description-list-item-label-width': labelWidth } : undefined
    "
    @click.capture="handleClick"
  >
    <dt v-if="label" ref="labelEl" class="description-list-item--label" v-text="label" />
    <dd>
      <span
        class="description-list-item--value"
        :class="{ 'description-list-item--empty': value == null }"
      >
        <template v-if="value !== null && typeof value === 'object' && value.type !== 'text'">
          <m-badge v-if="value?.type === 'badge'" v-bind="value" />
          <m-link v-if="value?.type === 'link'" v-bind="value" />
        </template>
        <template v-else-if="!$slots.text">
          {{
            value == null
              ? emptyValueText || "None recorded"
              : typeof value === "object"
                ? value.text || value
                : value
          }}
        </template>
        <slot name="text" />
      </span>
      <div v-if="badge" class="description-list-item--badge">
        <template v-if="Array.isArray(badge)">
          <template v-for="p in badge" :key="`${p}`">
            <m-badge v-if="typeof p === 'string'" :text="p" />
            <m-badge v-else v-bind="p" />
          </template>
        </template>
        <m-badge v-else-if="typeof badge === 'string'" :text="badge" />
        <m-badge v-else v-bind="badge" />
      </div>
      <div v-else></div>
      <m-action v-if="action" class="description-list-item--action" :model-value="action" />
      <div v-else></div>
    </dd>
  </div>
</template>
<style lang="scss" scoped>
/* ENG-21740 component-local: NHS-blue #003078 is foreign to the Medicus ramps. */
.description-list-item {
  --m-description-list-item-action-hover-text: #003078;

  display: grid;
  gap: var(--space-0-5);
  padding: var(--space-1) var(--space-4);
  break-inside: avoid;

  &.dark-label {
    .description-list-item--label {
      color: var(--text-color);
    }

    .description-list-item--value {
      color: var(--grey-darkest);
    }
  }

  &.has-click {
    cursor: pointer;

    &:hover {
      background-color: var(--surface-selected);

      .action {
        background: none;
        color: var(--m-description-list-item-action-hover-text);
      }
    }
  }

  &.has-vertical-line {
    border-left: 1px solid var(--border-subtle);

    position: relative;
    padding-left: var(--space-6) !important;

    &::before {
      content: " ";
      position: absolute;
      top: var(--space-1);
      left: var(--space-4);
      height: calc(100% - 10px);
      width: 3px;
      background-color: var(--status-blue);
    }
  }
  &.is-horizontal {
    @media (min-width: 768px) {
      display: grid;
      grid-template-columns: var(--description-list-item-label-width, 160px) 1fr;
    }

    padding: var(--space-1) 0;
    gap: var(--space-5);

    > dt {
      @media (min-width: 768px) {
        text-align: right;
      }
    }

    > dd {
      grid-template-areas: "value badge action";
      grid-template-rows: auto;

      padding-right: var(--space-1);

      .description-list-item--action {
        margin-top: var(--space-1);
      }

      // no <dt> rendered (empty label) — span both columns instead of collapsing into the label column
      &:first-child {
        @media (min-width: 768px) {
          grid-column: 1 / -1;
        }
      }
    }
  }
  > dt {
    color: var(--grey-darkest);
    font-size: var(--font-size-md);
    line-height: 150%;
  }
  > dd {
    display: grid;
    grid-template-columns: 1fr repeat(v-bind(extraRows), auto);
    grid-template-rows: auto auto;

    // ENG-21979 slotted lists own their indent here; without it they fall back to the browser's
    // 40px, which MLabeledText used to suppress before it was removed.
    :deep(ul),
    :deep(ol) {
      padding-inline-start: var(--space-4);
    }

    grid-template-areas:
      "value action"
      "badge action";

    .description-list-item--value {
      grid-area: value;
      word-break: break-word;
      hyphens: auto;
      white-space: pre-wrap;
      font-size: var(--font-size-md);
      line-height: 150%;
    }

    .description-list-item--badge {
      grid-area: badge;
      display: flex;
      align-items: center;
      gap: var(--space-2);
    }
    .description-list-item--action {
      grid-area: action;
      margin-left: var(--space-2);

      margin-top: calc(v-bind(contentMarginTop) + var(--space-2));
    }

    .description-list-item--empty {
      color: var(--grey-darkest);
    }
  }
}
</style>
